class CartDrawerSection extends HTMLElement {
  cartUpdateUnsubscriber = undefined;
  constructor() {
    super();

    this.cartType = this.dataset.cartType;
    this.drawerClass = "wt-cart__drawer";
    this.drawer = this.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--open`;
    this.pageOverlayClass = "page-overlay-cart";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;
    this.triggerQuery = [
      `.wt-cart__trigger`,
      `.wt-cart__back-link`,
      `.${this.pageOverlayClass}`,
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);
    this.isOpen = false;
    this.isCartPage = window.location.pathname === window.routes.cart_url;
    this.closeButton = () => this.querySelector(".wt-cart__drawer__close");
    this.mainTrigger = document.querySelector(".wt-cart__trigger");
    this.toggleEelements = () =>
      this.querySelectorAll(this.dataset.toggleTabindex);
  }

  connectedCallback() {
    if (this.cartType === "page" || this.isCartPage) {
      document.addEventListener('cart-drawer:refresh', (e) => this.refreshCartDrawer(e))
      return;
    }

    this.init();
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      if (this.isOpen) {
        setTabindex(this.toggleEelements(), "0");
        this.closeButton().focus();
      }
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input, select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
  }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      this.isOpen = false;
      this.mainTrigger.focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "-1");
    } else {
      this.setAttribute("open", "");
      this.isOpen = true;
      this.closeButton().focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "0");
    }
  }

  toggleDrawerClasses() {
    this.onToggle();
    this.drawer.classList.toggle(this.classDrawerActive);
    this.body.classList.toggle(this.activeOverlayBodyClass);
  }

  init() {
    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) {
          this.toggleDrawerClasses();
        }
      }

      if (isTabPressed) {
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });

    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggleDrawerClasses();
      });
    });

    this.addEventListener("click", (e) => {
      if (e.target.classList.contains("wt-cart__drawer__close")) {
        e.preventDefault();
        this.toggleDrawerClasses();
      }
    });

    document.addEventListener('cart-drawer:refresh', (e) => this.refreshCartDrawer(e))
  }

  renderContents(parsedState, isClosedCart = true) {
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector,
      );
    });

    if (isClosedCart) {
      setTimeout(() => {
        this.toggleDrawerClasses();
        if (this.isOpen) {
          this.closeButton().focus();
        }
      });
    }
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  refreshCartDrawer(e){
    const sectionsToRender = this.getSectionsToRender();
    fetch(`${window.Shopify.routes.root}?sections=${sectionsToRender[0].id},${sectionsToRender[1].id}`)
    .then(response => response.json())
    .then((response) => {
      const parsedState = {
        "sections": response
      }
      this.renderContents(parsedState, false);
    })
    .catch(e => {
      console.log(e)
    })
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawerSection);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);

(function () {
  async function getCart() {
    const response = await fetch("/cart.js", {
      headers: { Accept: "application/json" },
    });
    if (!response.ok) throw new Error("Cart fetch failed");
    return await response.json();
  }

  async function getProductByHandle(handle) {
    try {
      const response = await fetch(`/products/${handle}.js`, {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error(`Failed to load product handle: ${handle}`, error);
      return null;
    }
  }

  function formatMoney(cents) {
    return `Rs. ${(cents / 100).toFixed(2)}`;
  }

  async function addToCart(variantId) {
    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
      }),
    });

    if (!response.ok) {
      throw new Error("Add to cart failed");
    }

    document.dispatchEvent(new Event("cart:updated"));
  }

  async function injectCrossSell() {
    const container = document.querySelector("#CartDrawer-CrossSell");
    if (!container) return;

    try {
      const cart = await getCart();

      const productHandles = [
        "immaculate-gentle-cleanser",
        "daily-oil-free-moisturizer",
        "ceramide-hyaluronic-acid-moisturizer",
        "immaculate-ultra-matt-sunscreen-50-pa",
        "10-niacinamide-face-serum",
        "vitamin-c-10-face-serum",
        "alpha-arbutin-2-face-serum",
        "retinol-0-3-serum",
        "salicylic-acid-2-serum",
        "0-3-retinol-cream"
      ];

      const products = await Promise.all(
        productHandles.map((handle) => getProductByHandle(handle))
      );

      const cartProductIds = cart.items.map((item) => item.product_id);

      let filteredProducts = products.filter(
        (product) => product && !cartProductIds.includes(product.id)
      );

      filteredProducts.sort((a, b) => {
        if (a.handle === "immaculate-gentle-cleanser") return -1;
        if (b.handle === "immaculate-gentle-cleanser") return 1;
        return 0;
      });

      filteredProducts = filteredProducts.slice(0, 3);

      if (!filteredProducts.length) {
        container.innerHTML = `
          <div class="drawer-cross-sell">
            <h3 class="drawer-cross-sell__heading">Complete your routine</h3>
            <p style="font-size:12px; opacity:.7; margin:0;">
              No recommendation products available.
            </p>
          </div>
        `;
        return;
      }

      container.innerHTML = `
        <div class="drawer-cross-sell">
          <h3 class="drawer-cross-sell__heading">Complete your routine</h3>
          <div class="drawer-cross-sell__list">
            ${filteredProducts
              .map((product) => {
                const image = product.featured_image
                  ? `<img src="${product.featured_image}" alt="${product.title}" class="drawer-cross-sell__image">`
                  : `<div class="drawer-cross-sell__image drawer-cross-sell__image--placeholder"></div>`;

                const productUrl = product.url || `/products/${product.handle}`;
                const variantId = product.variants && product.variants.length
                  ? product.variants[0].id
                  : "";

                return `
                  <div class="drawer-cross-sell__card">
                    <a href="${productUrl}" class="drawer-cross-sell__image-link">
                      ${image}
                    </a>
                    <div class="drawer-cross-sell__content">
                      <a href="${productUrl}" class="drawer-cross-sell__title">${product.title}</a>
                      <div class="drawer-cross-sell__price">${formatMoney(product.price)}</div>
                      ${
                        variantId
                          ? `<button
                              type="button"
                              class="drawer-cross-sell__btn"
                              data-variant-id="${variantId}"
                            >
                              Add
                            </button>`
                          : `<span style="font-size:12px; opacity:.7;">Unavailable</span>`
                      }
                    </div>
                  </div>
                `;
              })
              .join("")}
          </div>
        </div>
      `;

      container.querySelectorAll(".drawer-cross-sell__btn").forEach((button) => {
        button.addEventListener("click", async function () {
          const variantId = this.getAttribute("data-variant-id");
          this.disabled = true;
          this.textContent = "Adding...";

          try {
            await addToCart(variantId);
            setTimeout(injectCrossSell, 300);
          } catch (error) {
            console.error("Add to cart failed:", error);
            this.disabled = false;
            this.textContent = "Add";
          }
        });
      });
    } catch (error) {
      console.error("Cross-sell error:", error);
      container.innerHTML = `
        <div class="drawer-cross-sell">
          <h3 class="drawer-cross-sell__heading">Complete your routine</h3>
          <p style="font-size:12px; color:#b00020; margin:0;">
            Cross-sell failed to load.
          </p>
        </div>
      `;
    }
  }

  function initCrossSell() {
    injectCrossSell();

    const observer = new MutationObserver(() => {
      if (document.querySelector("#CartDrawer-CrossSell")) {
        injectCrossSell();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    document.addEventListener("cart:updated", () => {
      setTimeout(injectCrossSell, 200);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initCrossSell);
  } else {
    initCrossSell();
  }
})();

